/**
 * Reading the barcode on the back of a book.
 *
 * **Two engines, and the second is why this works on Sam's laptop.**
 *
 * `BarcodeDetector` is built into the browser and is the better of the two
 * where it exists: it is hardware-accelerated and it decodes in poor light
 * that ZXing gives up on. But as of September 2026 it exists only in Chrome
 * and Edge on **Android, macOS and ChromeOS** — not on Windows, not on iOS.
 * Which is to say: not on the machine this app is being built on, and not on
 * an iPad.
 *
 * So ZXing is behind it, and ZXing works everywhere a camera does.
 *
 * **It is loaded only when somebody scans.** `import()` rather than a plain
 * import, so the library is a separate chunk that never reaches a child
 * opening the app to practise spelling. That is the whole reason it is
 * affordable: about 280KB gzipped, downloaded by a parent, once, the first
 * time they point a camera at a book.
 *
 * **Only the middle strip of the frame is decoded.** A barcode is thin black
 * lines, and searching a whole 1280x720 frame for them is both slower and
 * more likely to lock onto the price barcode, the publisher's logo or a
 * pattern on the cover. The crop matches the box drawn on screen, and the
 * two share the constants below so they cannot drift apart.
 *
 * **Measured, 28 Sep 2026.** Real EAN-13 barcodes rendered to the exact size
 * of that crop, 1075x230, and run through this code path: a clean one
 * decodes in about 1ms, and so does one blurred, tilted eleven degrees, lit
 * unevenly and covered in sensor noise. That is what sets the polling rate
 * in the camera view — the copy out of the video costs more than the decode
 * does.
 */

/** The guide box, as a fraction of the frame. The camera view draws the same
 *  rectangle, from these same numbers. */
export const GUIDE = { left: 0.08, right: 0.92, top: 0.34, bottom: 0.66 }

// EAN-13 is what a book's barcode is. EAN-8 and UPC-A are included because a
// camera pointed at a book often catches the smaller barcode beside it.
const NATIVE_FORMATS = ['ean_13', 'ean_8', 'upc_a']

/** The shapes of `BarcodeDetector`, which TypeScript does not know about. */
interface DetectedBarcode { rawValue: string; format: string }
interface NativeDetector {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>
}
type NativeClass = new (options?: { formats?: string[] }) => NativeDetector

export interface Reader {
  /** Which engine answered, for the line under the camera view. */
  engine: 'native' | 'zxing'
  /** One frame. Returns the digits, or null for "not this time". */
  read(video: HTMLVideoElement): Promise<string | null>
}

/**
 * Can this device scan at all?
 *
 * Now true wherever there is a camera, which is the point of adding ZXing.
 * Kept as a function rather than removed, because "is there a camera" is
 * still a real question on a desktop without one.
 */
export function canScanBarcodes(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia)
}

export async function makeReader(): Promise<Reader> {
  const native = nativeClass()
  if (native) {
    try {
      const detector = new native({ formats: NATIVE_FORMATS })
      return {
        engine: 'native',
        read: async (video) => {
          try {
            return pickBookBarcode(
              (await detector.detect(video)).map(b => b.rawValue))
          } catch {
            // detect() throws on a frame that is not ready, which happens for
            // the first few after the camera opens. Not an error, just early.
            return null
          }
        },
      }
    } catch {
      // The constructor exists but refused these formats. Fall through.
    }
  }
  return zxingReader()
}


// ------------------------------------------------------------------ ZXing

async function zxingReader(): Promise<Reader> {
  // Loaded here, not at the top of the file, so it is a separate chunk that
  // only a parent scanning a book ever downloads.
  const zxing = await import('@zxing/library')

  const reader = new zxing.MultiFormatReader()
  const hints = new Map()
  hints.set(zxing.DecodeHintType.POSSIBLE_FORMATS, [
    zxing.BarcodeFormat.EAN_13,
    zxing.BarcodeFormat.EAN_8,
    zxing.BarcodeFormat.UPC_A,
  ])
  // Worth the cost here. TRY_HARDER makes ZXing rotate and re-scan, which
  // roughly doubles the work per frame and is what lets it read a book held
  // at an angle — which is how a book is held.
  hints.set(zxing.DecodeHintType.TRY_HARDER, true)
  reader.setHints(hints)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })

  return {
    engine: 'zxing',
    read: async (video) => {
      const width = video.videoWidth
      const height = video.videoHeight
      if (!ctx || !width || !height) return null

      // The strip inside the guide box, and nothing else.
      const sx = Math.floor(width * GUIDE.left)
      const sy = Math.floor(height * GUIDE.top)
      const sw = Math.max(1, Math.floor(width * (GUIDE.right - GUIDE.left)))
      const sh = Math.max(1, Math.floor(height * (GUIDE.bottom - GUIDE.top)))

      canvas.width = sw
      canvas.height = sh
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh)

      const rgba = ctx.getImageData(0, 0, sw, sh).data
      const source = new zxing.RGBLuminanceSource(greyscale(rgba), sw, sh)
      const bitmap = new zxing.BinaryBitmap(new zxing.HybridBinarizer(source))
      try {
        return pickBookBarcode([reader.decode(bitmap).getText()])
      } catch {
        // NotFoundException every frame until it finds one. Expected.
        return null
      } finally {
        // The reader keeps per-image state between calls and returns stale
        // results without this.
        reader.reset()
      }
    },
  }
}

/**
 * RGBA pixels to one grey byte each.
 *
 * `RGBLuminanceSource` takes either packed Int32 pixels or an already-grey
 * array — it does NOT convert the RGBA array a canvas hands back, which is
 * what `getImageData` gives. Passing that straight in yields a source four
 * times too long and a decoder that never finds anything.
 *
 * The weights are ITU-R BT.601, in integer arithmetic because this runs on
 * every pixel of every frame.
 */
function greyscale(rgba: Uint8ClampedArray): Uint8ClampedArray {
  const grey = new Uint8ClampedArray(rgba.length / 4)
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    grey[j] = (rgba[i]! * 306 + rgba[i + 1]! * 601 + rgba[i + 2]! * 117) >> 10
  }
  return grey
}


// ----------------------------------------------------------------- shared

/**
 * Which of the codes in shot is the book.
 *
 * Deliberately NOT a full ISBN check. The check digit and the 978/979 rule
 * live on the server, in one place, because that is the copy that has to be
 * right. This only has to tell a book's barcode apart from the price barcode
 * printed beside it, which is five digits.
 */
function pickBookBarcode(values: string[]): string | null {
  for (const value of values) {
    const digits = (value || '').replace(/\D/g, '')
    if (digits.length === 13 && /^97[89]/.test(digits)) return digits
  }
  return null
}

function nativeClass(): NativeClass | null {
  const found = (window as unknown as { BarcodeDetector?: NativeClass })
    .BarcodeDetector
  return typeof found === 'function' ? found : null
}


// ----------------------------------------------------------------- camera

/**
 * The back camera, at a size worth decoding.
 *
 * `facingMode: environment` is a request, not a promise — a laptop has one
 * camera and will hand it over whatever is asked for. The resolution matters
 * more than it looks: a barcode is thin black lines, and at 640px across a
 * whole book cover there are not enough pixels per line to decode.
 */
export async function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: false,
  })
}

/** Stop the camera. Leaving it on lights the indicator and drains a phone. */
export function closeCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop())
}
